import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, downloadHref, assetFilename } from "../lib/api";
import { useConfig } from "../context/ConfigContext";
import ContentPreview from "../components/ContentPreview";
import MarkDoneDialog from "../components/MarkDoneDialog";
import { toast } from "sonner";
import {
  Wand2, Sparkles, FileText, ImagePlus, Film, Video, Mail, MessageSquare,
  Loader2, Download, ImageIcon, RefreshCw,
} from "lucide-react";

const TYPES = [
  { type: "post", label: "Post", icon: FileText },
  { type: "image", label: "Image", icon: ImagePlus },
  { type: "story", label: "Story", icon: Film },
  { type: "reel", label: "Reel", icon: Video },
  { type: "email", label: "Email", icon: Mail },
  { type: "text", label: "Text", icon: MessageSquare },
];
const GOALS = ["Let Marketing Director Decide", "Build excitement", "Get reservations", "Explain value", "Answer a question", "Show a hotel", "Show an experience", "Explain payment", "Build trust"];
const FEATURES = ["Let Marketing Director Decide", "Bangkok", "Phuket", "Bangkok Marriott Marquis Queen's Park", "Grand Mercure Phuket Patong", "$299 deposit", "Payment plan", "Itinerary / value", "Optional experience", "Community", "FAQ"];
const CTAS = ["Let Marketing Director Decide", "Reserve Your Spot", "Learn More", "Share With a Friend", "Ask Us a Question"];
const TWEAKS = ["Make it more exciting", "Make it more personal", "Make it less salesy", "Give me another idea", "Shorten it"];

export default function Create() {
  const { config } = useConfig();
  const loc = useLocation();
  const [type, setType] = useState("post");
  const [audience, setAudience] = useState("Everyone");
  const [goal, setGoal] = useState("Let Marketing Director Decide");
  const [feature, setFeature] = useState("Let Marketing Director Decide");
  const [cta, setCta] = useState("Let Marketing Director Decide");
  const [selMedia, setSelMedia] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {content, why}
  const [markOpen, setMarkOpen] = useState(false);
  const [genAsset, setGenAsset] = useState(null); // {url, kind}
  const [assetLoading, setAssetLoading] = useState(false);

  const doGenerate = useCallback(async (spec, auto) => {
    setLoading(true);
    setResult(null);
    setGenAsset(null);
    try {
      const body = {
        content_type: spec?.content_type || type,
        audience: spec?.audience || audience,
        goal: spec?.goal || goal,
        feature: spec?.feature || feature,
        cta: spec?.cta || cta,
        pillar: spec?.pillar || "",
        theme: spec?.theme || "",
        platform: spec?.platform || "",
        media_ids: selMedia,
        auto: !!auto,
      };
      const r = await api.generate(body);
      setResult(r);
      if (spec?.content_type) setType(spec.content_type);
      toast.success("Your Marketing Director wrote it.");
    } catch (e) {
      toast.error("Generation failed. Check your Universal Key balance.");
    } finally {
      setLoading(false);
    }
  }, [type, audience, goal, feature, cta, selMedia]);

  useEffect(() => {
    api.media().then(setMedia).catch(() => {});
  }, []);

  // handle prefill from Today
  useEffect(() => {
    const st = loc.state;
    if (st && (st.spec || st.auto)) {
      if (st.spec?.content_type) setType(st.spec.content_type);
      if (st.spec?.audience) setAudience(st.spec.audience);
      doGenerate(st.spec, st.auto);
      window.history.replaceState({}, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const images = media.filter((m) => m.kind === "image");

  const transform = async (instruction, target) => {
    if (!result?.content) return;
    setLoading(true);
    try {
      const r = await api.transform({ content_id: result.content.id, instruction, target_type: target });
      setResult({ content: r.content, why: result.why });
      setGenAsset(null);
      if (target) setType(target);
      toast.success("Updated");
    } catch { toast.error("Transform failed"); } finally { setLoading(false); }
  };

  const renderGraphic = async () => {
    if (!result?.content) return;
    setAssetLoading(true);
    const g = result.content.generated;
    try {
      const r = await api.genImage({
        content_id: result.content.id,
        overline: "THE PASSPORT EXPERIENCE™",
        headline: g.headline || result.content.title || "Thailand 2027",
        subhead: g.subhead || (config?.facts ? `${config.facts.destinations} · ${config.facts.dates}` : ""),
        cta: g.cta || (cta !== "Let Marketing Director Decide" ? cta : "Reserve Your Spot"),
        footer: `$${config?.facts?.deposit || 299} reserves your spot · ${config?.facts?.url || ""}`,
        media_id: selMedia[0] || null,
        size: [1080, 1920],
      });
      setGenAsset({ url: r.url, kind: "image" });
      toast.success("Branded graphic created (1080×1920)");
    } catch { toast.error("Graphic render failed"); } finally { setAssetLoading(false); }
  };

  const renderReel = async () => {
    if (!result?.content) return;
    setAssetLoading(true);
    const g = result.content.generated;
    const genScenes = (g.scenes || []).length ? g.scenes : [
      { text_overlay: "Thailand 2027", seconds: 3 },
      { text_overlay: g.hook || "Bangkok + Phuket", seconds: 3 },
      { text_overlay: g.cover_text || "Reserve Your Spot", seconds: 3 },
    ];
    const scenes = genScenes.slice(0, 6).map((s, i) => ({
      media_id: images[i % Math.max(images.length, 1)]?.id || null,
      headline: s.text_overlay || "",
      subhead: "",
      cta: i === genScenes.length - 1 ? (g.cta || "Reserve Your Spot") : "",
      seconds: s.seconds || 3,
    }));
    try {
      const r = await api.genReel({ content_id: result.content.id, title: result.content.title, scenes });
      if (r.success) {
        setGenAsset({ url: r.url, kind: "video", spec: r.spec });
        toast.success("MP4 rendered (no-music master)");
      } else {
        toast.error(r.message || "Reel render unavailable");
      }
    } catch { toast.error("Reel render failed"); } finally { setAssetLoading(false); }
  };

  const markPosted = async (payload) => {
    setMarkOpen(false);
    const c = result.content;
    await api.completeRec({
      action_type: ({ post: "Facebook post", story: "Instagram Story", reel: "Reel", email: "Email", text: "Text blast", image: "Instagram post" })[c.content_type] || "Facebook post",
      platform: c.platform, audience: c.audience, pillar: c.pillar, theme: c.theme,
      cta: c.cta, copy: c.caption, content_id: c.id, notes: payload.notes, link: payload.link,
    });
    await api.updateContent(c.id, { status: "Published" });
    toast.success("Marked done. Tomorrow's plan will adjust.");
  };

  const toggleMedia = (id) => setSelMedia((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow text-[#D4AF37]">Content Studio</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-1">What are we creating?</h1>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-5">
          <div className="card-lux p-5">
            <div className="grid grid-cols-3 gap-2 mb-5">
              {TYPES.map((t) => (
                <button key={t.type} data-testid={`type-${t.type}`} onClick={() => { setType(t.type); setResult(null); setGenAsset(null); }}
                  className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border text-xs ${type === t.type ? "border-[#D4AF37] bg-[#222736] text-[#F3E5AB]" : "border-[#2A3042] text-[#94A3B8] hover:border-[#D4AF37]/40"}`}>
                  <t.icon className="w-4 h-4" /> {t.label}
                </button>
              ))}
            </div>

            <Select label="Who is this for?" value={audience} onChange={setAudience} testid="sel-audience"
              options={["Everyone", ...(config?.audiences || [])]} />
            <Select label="What should it do?" value={goal} onChange={setGoal} testid="sel-goal" options={GOALS} />
            <Select label="What should we feature?" value={feature} onChange={setFeature} testid="sel-feature" options={FEATURES} />
            <Select label="CTA" value={cta} onChange={setCta} testid="sel-cta" options={CTAS} />

            <div className="mt-4 space-y-2">
              <button data-testid="create-it-for-me" onClick={() => doGenerate(null, true)} disabled={loading}
                className="gold-btn w-full py-3 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Create it for me
              </button>
              <button data-testid="generate-btn" onClick={() => doGenerate({ content_type: type, audience, goal, feature, cta }, false)} disabled={loading}
                className="w-full py-2.5 rounded-lg border border-[#2A3042] text-[#94A3B8] hover:text-[#F3E5AB] hover:border-[#D4AF37]/40 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                <Wand2 className="w-4 h-4" /> Generate with my choices
              </button>
            </div>
          </div>

          {/* media picker */}
          <div className="card-lux p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow text-[#D4AF37]">Attach approved media</div>
              <span className="text-xs text-[#64748B]">{selMedia.length} selected</span>
            </div>
            {images.length === 0 ? (
              <p className="text-sm text-[#64748B]">No media yet. Upload logos & Thailand photos in the Media Library.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto">
                {images.map((m) => (
                  <button key={m.id} data-testid={`media-pick-${m.id}`} onClick={() => toggleMedia(m.id)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 ${selMedia.includes(m.id) ? "border-[#D4AF37]" : "border-transparent"}`}>
                    <img src={m.url} alt={m.title} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: result */}
        <div className="lg:col-span-3">
          <div className="card-lux p-6 min-h-[400px]">
            {loading && (
              <div className="flex flex-col items-center justify-center h-80 text-[#94A3B8]">
                <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mb-3" />
                <p className="text-sm">Your Marketing Director is writing…</p>
              </div>
            )}
            {!loading && !result && (
              <div className="flex flex-col items-center justify-center h-80 text-center text-[#64748B]">
                <Wand2 className="w-10 h-10 mb-3 text-[#2A3042]" />
                <p className="max-w-xs text-sm">Pick a type and press <span className="text-[#F3E5AB]">Create it for me</span> — or let the Marketing Director decide everything.</p>
              </div>
            )}
            {!loading && result && (
              <>
                <ContentPreview content={result.content} />

                {/* Asset generation */}
                {(type === "image" || type === "story") && (
                  <div className="mt-5 pt-5 border-t border-[#2A3042]">
                    <button data-testid="render-graphic-btn" onClick={renderGraphic} disabled={assetLoading}
                      className="gold-btn px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-60">
                      {assetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Create the 1080×1920 graphic
                    </button>
                  </div>
                )}
                {type === "reel" && (
                  <div className="mt-5 pt-5 border-t border-[#2A3042]">
                    <button data-testid="render-reel-btn" onClick={renderReel} disabled={assetLoading}
                      className="gold-btn px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-60">
                      {assetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Render MP4 (no-music master)
                    </button>
                    <p className="text-xs text-[#64748B] mt-2">Uses your selected media as scene backgrounds. 1080×1920 · H.264 · 30fps · silent master to add music in Instagram/Facebook.</p>
                  </div>
                )}

                {genAsset && (
                  <div className="mt-5 p-4 rounded-lg bg-[#12151E] border border-[#2A3042]" data-testid="generated-asset">
                    <div className="eyebrow text-[#D4AF37] mb-2">Generated asset</div>
                    {genAsset.kind === "image" ? (
                      <img src={genAsset.url} alt="graphic" className="max-h-96 rounded-lg mx-auto" />
                    ) : (
                      <video src={genAsset.url} controls playsInline className="max-h-96 rounded-lg mx-auto w-auto" />
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                      <a data-testid="download-asset-btn"
                        href={downloadHref(genAsset.url, assetFilename(genAsset.kind, result.content.title, genAsset.kind === "image" ? "png" : "mp4"))}
                        className="gold-btn py-3 rounded-lg inline-flex items-center justify-center gap-2 text-sm">
                        <Download className="w-4 h-4" /> {genAsset.kind === "image" ? "Download image" : "Download video (MP4)"}
                      </a>
                      {result.content.generated?.caption && (
                        <button data-testid="copy-caption-asset-btn"
                          onClick={() => { navigator.clipboard.writeText(result.content.generated.caption); toast.success("Caption copied"); }}
                          className="py-3 rounded-lg border border-[#2A3042] text-[#94A3B8] hover:text-[#F3E5AB] hover:border-[#D4AF37]/40 inline-flex items-center justify-center gap-2 text-sm">
                          Copy caption
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Manual overrides + mark done */}
                <div className="mt-6 pt-5 border-t border-[#2A3042]">
                  <div className="eyebrow text-[#94A3B8] mb-2">Make it yours</div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {TWEAKS.map((t) => (
                      <button key={t} data-testid={`tweak-${t}`} onClick={() => transform(t)} disabled={loading}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#2A3042] text-[#94A3B8] hover:text-[#F3E5AB] hover:border-[#D4AF37]/40 inline-flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" /> {t}
                      </button>
                    ))}
                    {type !== "story" && <button onClick={() => transform("Turn this into an Instagram Story", "story")} className="text-xs px-3 py-1.5 rounded-full border border-[#2A3042] text-[#94A3B8] hover:text-[#F3E5AB]">Turn into Story</button>}
                    {type !== "reel" && <button onClick={() => transform("Turn this into a Reel", "reel")} className="text-xs px-3 py-1.5 rounded-full border border-[#2A3042] text-[#94A3B8] hover:text-[#F3E5AB]">Turn into Reel</button>}
                  </div>
                  <button data-testid="mark-posted-btn" onClick={() => setMarkOpen(true)}
                    className="w-full py-2.5 rounded-lg bg-[#10B981]/15 border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/25 text-sm font-medium">
                    Mark posted / done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <MarkDoneDialog open={markOpen} onOpenChange={setMarkOpen} onConfirm={markPosted} title="Mark this content posted" />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div className="mb-3">
      <label className="text-xs text-[#94A3B8]">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[#0A0C10] border border-[#2A3042] outline-none focus:border-[#D4AF37] text-sm text-[#F8FAFC]">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
